import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Hospital } from "lucide-react";

const MARKEL_COLOR = "#F26522";

const MarkelTarif = () => {
  const caseStudies = [
    {
      title: "Ransomware-Angriff auf Gemeinschaftspraxis",
      description: "Locky-Trojaner verschlüsselte alle Patientendaten über das Wochenende. 120 Termine mussten verschoben werden. Praxisbetrieb stand still.",
      outcome: "Markel-Notfall-Hotline binnen 3 Stunden aktiv. IT-Forensiker analysierten Angriff, bereiteten Daten aus Backup wieder her. Nach 4 Tagen voller Betrieb. Versicherung übernahm 52.300 € (IT-Forensik, Datenrettung, Umsatzausfall, Krisenberatung, Patienteninformation).",
      damage: "52.300 €"
    },
    {
      title: "DSGVO-Verstoß durch Datenleck",
      description: "Ungesicherte Cloud-Freigabe führte zu öffentlichem Zugriff auf 450 Patientenakten. Whistleblower meldete Vorfall an Datenschutzbehörde.",
      outcome: "Markel koordinierte Sofortmaßnahmen: Rechtsberatung, Behördenmeldung, Patienteninformation. Bußgeld durch professionelles Krisenmanagement auf 8.000 € begrenzt. Versicherung trug Bußgeld plus 6.200 € Anwaltskosten. Praxis erhielt DSGVO-Compliance-Beratung.",
      damage: "14.200 €"
    },
    {
      title: "Business Email Compromise (BEC)",
      description: "Gefälschte Rechnung mit veränderten Bankdaten führte zu Überweisung von 28.000 € an Betrüger. Betrug erst nach 5 Tagen entdeckt.",
      outcome: "Markel aktivierte Sofortmaßnahmen: Strafanzeige, forensische Analyse, Rückholungsversuch über Banken. 12.000 € konnten zurückgeholt werden. Versicherung erstattete verbleibende 16.000 € plus 4.800 € Forensikkosten.",
      damage: "20.800 €"
    }
  ];

  const coverageAreas = [
    {
      icon: Shield,
      title: "Eigenschäden",
      items: [
        "IT-Forensik und Schadensanalyse",
        "Datenwiederherstellung nach Cyberangriff",
        "Betriebsunterbrechung & Umsatzausfall",
        "System- und Softwarewiederherstellung",
        "Cyber-Erpressung (Ransomware)"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cyber-Haftpflicht",
      items: [
        "DSGVO-Bußgelder und Schadensersatz",
        "Datenschutzverletzungen & Meldepflichten",
        "Ansprüche Dritter bei Datenlecks",
        "Abwehr unberechtigter Ansprüche",
        "Rechtsschutz bei Cyber-Vorfällen"
      ]
    },
    {
      icon: Phone,
      title: "24/7 Assistance",
      items: [
        "Cyber-Notfall-Hotline rund um die Uhr",
        "Sofortige Expertenunterstützung",
        "Koordination aller Dienstleister",
        "PR- und Krisenkommunikation",
        "Persönlicher Schadenmanager"
      ]
    },
    {
      icon: CheckCircle2,
      title: "Weltweiter Schutz",
      items: [
        "Versicherungsschutz weltweit gültig",
        "Keine geografischen Einschränkungen",
        "Auch bei Cloud-Servern im Ausland",
        "Unbegrenzte Sublimits-Struktur",
        "Flexible Deckungssummen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausarztpraxen",
      description: "Einzelpraxen mit hoher Patientenfrequenz und sensiblen Gesundheitsdaten",
      benefits: [
        "Weltweiter Schutz auch bei Cloud-Nutzung im Ausland",
        "Unbegrenzte Sublimits für maximale Schadensflexibilität",
        "24/7-Hotline für schnelle Hilfe im akuten Notfall",
        "Bündelrabatte mit Berufshaftpflicht verfügbar"
      ],
      recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "Facharztpraxen",
      description: "Spezialisierte Praxen mit umfangreichen digitalen Diagnosesystemen",
      benefits: [
        "Umfassender Schutz teurer medizinischer IT-Systeme",
        "Absicherung bei Ausfall bildgebender Verfahren",
        "DSGVO-Schutz bei Datenübermittlung an Dritte",
        "Betriebsunterbrechungsdeckung vollständig inkludiert"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
    },
    {
      icon: Building2,
      title: "MVZ & Praxen",
      description: "Mehrere Standorte mit vernetzten IT-Systemen",
      benefits: [
        "Alle Standorte zentral unter einem Vertrag versichert",
        "Weltweiter Schutz für internationale Datenströme",
        "Höhere Deckungssummen bis zu 5 Millionen Euro",
        "Persönlicher Schadenmanager für komplexe Fälle"
      ],
      recommendation: "Empfohlene Deckung: 2.000.000 - 3.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
      benefits: [
        "Maximalschutz mit Deckung bis zu 5 Millionen Euro",
        "Sofortige Krisenintervention bei akuten Großschäden",
        "Unbegrenzte Sublimits für sämtliche Schadenskategorien",
        "Spezialisiertes Krisenmanagement rund um die Uhr"
      ],
      recommendation: "Empfohlene Deckung: 3.000.000 - 5.000.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Selbstbehalt",
      description: "Standard: 2.500 € pro Schadensfall. Optional reduzierbar auf 1.000 € oder erhöhbar auf 5.000 € für niedrigere Prämie."
    },
    {
      icon: CheckCircle2,
      title: "Deckungssummen",
      description: "Flexibel: 250.000 € bis 5.000.000 €. Einzelpraxen: 500.000 - 1.000.000 €, MVZ/Kliniken: 2.000.000 - 5.000.000 €"
    },
    {
      icon: Phone,
      title: "Service & Support",
      description: "24/7 Cyber-Notfall-Hotline, persönlicher Schadenmanager, kostenlose Risikobewertung"
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "Mindestlaufzeit: 1 Jahr, automatische Verlängerung, Sonderkündigungsrecht nach Schaden"
    },
    {
      icon: FileText,
      title: "Kündigungsfrist",
      description: "3 Monate zum Vertragsende, Sonderkündigung nach Schadensfall, beidseitiges Kündigungsrecht"
    },
    {
      icon: AlertCircle,
      title: "Besonderheiten",
      description: "Weltweiter Versicherungsschutz, unbegrenzte Sublimits, Bündelrabatte möglich"
    }
  ];

  const faqs = [
    {
      question: "Was macht die Markel Cyberversicherung für medizinische Praxen besonders?",
      answer: "Markel ist spezialisiert auf professionelle Dienstleister und bietet maßgeschneiderte Cyber-Lösungen für Arztpraxen. Besonders hervorzuheben sind die unbegrenzte Sublimits-Struktur, weltweiter Versicherungsschutz und die Integration mit Berufshaftpflicht zu vergünstigten Konditionen."
    },
    {
      question: "Was bedeutet 'unbegrenzte Sublimits'?",
      answer: "Anders als bei vielen Wettbewerbern gibt es bei Markel keine Untergrenzen für einzelne Schadenskategorien. Die gesamte Deckungssumme steht für jeden Schadenbereich zur Verfügung - maximale Flexibilität im Schadensfall."
    },
    {
      question: "Ist weltweiter Schutz wirklich wichtig für meine Praxis?",
      answer: "Ja! Viele Cyber-Angriffe kommen aus dem Ausland. Auch wenn Sie im Urlaub sind und dort mit Praxisdaten arbeiten oder Ihre Cloud-Server im Ausland stehen, sind Sie bei Markel abgesichert."
    },
    {
      question: "Welche Deckungssummen bietet Markel?",
      answer: "Markel bietet flexible Deckungssummen von 250.000 € bis 5 Millionen €. Für Einzelpraxen empfehlen wir mindestens 500.000 €, für MVZ und Kliniken 2-5 Millionen €."
    },
    {
      question: "Kann ich die Markel Cyberversicherung mit anderen Versicherungen kombinieren?",
      answer: "Ja, Markel bietet attraktive Bündelrabatte bei Kombination mit Berufshaftpflicht, Betriebshaftpflicht oder Praxisausfallversicherung. Die Verwaltung erfolgt zentral über einen Ansprechpartner."
    },
    {
      question: "Wie schnell greift der Versicherungsschutz?",
      answer: "Der Versicherungsschutz beginnt sofort ab Vertragsbeginn ohne Wartezeit. Im Schadensfall erreichen Sie die 24/7-Notfall-Hotline, die innerhalb von Stunden erste Maßnahmen koordiniert."
    },
    {
      question: "Werden Ransomware-Lösegeldzahlungen übernommen?",
      answer: "Ja, Markel übernimmt nach sorgfältiger Prüfung auch Lösegeldzahlungen bei Ransomware-Angriffen. Zusätzlich werden alle damit verbundenen Kosten (IT-Forensik, Verhandlungen, Datenrettung) erstattet."
    }
  ];

  const documents = [
    {
      title: "Markel Cyber Produktinformation",
      type: "PDF, ca. 750 KB",
      url: "/erstinformation.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF, ca. 1,1 MB",
      url: "/erstinformation.pdf"
    }
  ];

  const heroFeatures = [
    { icon: Shield, title: "Weltweiter Schutz", subtitle: "Ohne Limit" },
    { icon: CheckCircle2, title: "Unbegr. Sublimits", subtitle: "Volle Flexibilität" },
    { icon: Phone, title: "24/7 Hotline", subtitle: "Sofort erreichbar" }
  ];

  return (
    <BaseTariffPage
      companyName="Markel"
      companyColor={MARKEL_COLOR}
      productRating={{ value: 4.7, count: 389 }}
      priceRange="60-215"
      pageTitle="Markel Cyberversicherung für Ärzte & Praxen 2025 | Weltweiter Schutz & Unbegrenzte Sublimits"
      metaDescription="Markel Cyberversicherung mit weltweitem Schutz, unbegrenzten Sublimits & 24/7-Hotline. Spezialist für medizinische Berufe. Bündelrabatte möglich. Jetzt beraten lassen!"
      metaKeywords="Markel Cyberversicherung, Cyber-Versicherung Ärzte, Markel Praxis Cyberschutz, weltweiter Cyber-Schutz, unbegrenzte Sublimits"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/markel"
      heroLabel="Spezialist für professionelle Dienstleister"
      heroTitle="Markel Cyberversicherung für medizinische Praxen"
      heroDescription="Weltweiter Cyber-Schutz mit unbegrenzten Sublimits und 24/7-Notfall-Hotline. Spezialisiert auf Arztpraxen, MVZ und Kliniken. Attraktive Bündelrabatte bei Kombination mit anderen Versicherungen."
      heroFeatures={heroFeatures}
      alertTitle="Warum Markel?"
      alertDescription="Als Spezialist für professionelle Dienstleister bietet Markel maßgeschneiderte Cyber-Lösungen für Arztpraxen. Besonders hervorzuheben: Unbegrenzte Sublimits bedeuten maximale Flexibilität im Schadensfall - die gesamte Deckungssumme steht für jeden Schadenbereich zur Verfügung. Weltweiter Schutz sichert Sie auch bei ausländischen Cloud-Servern oder Auslandsaufenthalten ab."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
      caseStudies={caseStudies}
    />
  );
};

export default MarkelTarif;
